import React from 'react';
import { Box, Button, Typography, Stack, TextField, ToggleButton, ToggleButtonGroup, FormControl, InputLabel, Select, MenuItem, Checkbox } from '@mui/material';
import { Add } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import HelpTooltip from '../../../../components/HelpTooltip';

const CATEGORIES = ['all', 'cosmetic', 'mechanical', 'other'];

const PresetFilters = ({ searchTerm, setSearchTerm, selectedCategories, setSelectedCategories, selectedDealerships, setSelectedDealerships, openPresetModal }) => {
  const related_installations = useSelector(state => state.settings_dealerships?.related_installations || []);

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6">Presets</Typography>
        <Stack direction="row" alignItems="center">
          <Button variant="outlined" size="small" startIcon={<Add />} onClick={() => openPresetModal()}>
            Add Preset
          </Button>
          <HelpTooltip title="Create a new reusable work item with pricing, category, and layout." />
        </Stack>
      </Stack>
      <Box sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Dealerships</InputLabel>
            <Select
              multiple
              value={selectedDealerships}
              onChange={(e) => {
                const newValue = Array.isArray(e.target.value) ? e.target.value : [];
                setSelectedDealerships(newValue.length === 0 ? ['all'] : newValue);
              }}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.includes('all') || selected.length === 0 ? (
                    <Box sx={{ color: 'text.secondary' }}>All</Box>
                  ) : (
                    selected.map(subdomain => {
                      const inst = related_installations.find(i => i.subdomain === subdomain);
                      return (
                        <Box key={subdomain} sx={{ display: 'inline-flex', alignItems: 'center', mr: 0.5 }}>
                          {inst?.softname || subdomain}
                        </Box>
                      );
                    })
                  )}
                </Box>
              )}
            >
              <MenuItem value="all">
                <Checkbox checked={selectedDealerships.includes('all')} />
                <em>All</em>
              </MenuItem>
              {related_installations.map(inst => (
                <MenuItem key={inst.subdomain} value={inst.subdomain}>
                  <Checkbox checked={selectedDealerships.includes(inst.subdomain)} />
                  {inst.softname || inst.subdomain}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <HelpTooltip title="Filter presets by dealership. 'All' shows presets for all dealerships or those with no specific dealership assigned." />
        </Stack>
        <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search presets..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <HelpTooltip title="Search presets by name. Matches are highlighted instantly." />
        </Stack>
        <Stack direction="row" alignItems="center">
          <ToggleButtonGroup
            value={selectedCategories}
            exclusive={false}
            onChange={(event, newValue) => {
              if (!newValue || newValue.length === 0) {
                setSelectedCategories(['all']);
                return;
              }
              const clickedValue = event.target.value;
              if (clickedValue === 'all') {
                setSelectedCategories(['all']);
                return;
              }
              if (selectedCategories.includes('all')) {
                setSelectedCategories([clickedValue]);
                return;
              }
              const updated = selectedCategories.includes(clickedValue)
                ? selectedCategories.filter(c => c !== clickedValue)
                : [...selectedCategories, clickedValue];
              setSelectedCategories(updated.length > 0 ? updated : ['all']);
            }}
            sx={{ flexWrap: 'wrap', gap: 0.5 }}
          >
            {CATEGORIES.map(cat => (
              <ToggleButton key={cat} value={cat} sx={{ textTransform: 'capitalize' }}>
                {cat === 'all' ? 'All' : cat.charAt(0).toUpperCase() + cat.slice(1)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <HelpTooltip title="Filter presets by category. 'All' shows everything." />
        </Stack>
      </Box>
    </>
  );
};

export default PresetFilters;